import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  Box,
  LinearProgress,
  Typography,
  Divider,
  Collapse,
  IconButton,
  Chip,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { DecisionFactor, PredictProgram } from '~/types/predict';

interface ProgramFitCardProps {
  program: PredictProgram;
  score: number;
  decisionFactors: DecisionFactor[];
  isRecommended?: boolean;
  isSelected?: boolean;
  onSelect?: (program: PredictProgram) => void;
  onClearSelection?: () => void;
}

/**
 * Program Fit Card Component
 *
 * Displays a single program with its fit score and the AI-generated decision
 * factors that contributed to the score, ranked by importance with visual
 * indicators and explanations.
 */
const ProgramFitCard: React.FC<ProgramFitCardProps> = ({
  program,
  score,
  decisionFactors,
  isRecommended = false,
  isSelected = false,
  onSelect,
  onClearSelection,
}) => {
  const [expanded, setExpanded] = useState(isRecommended || isSelected); // Auto-expand recommended or selected program

  // Sort factors by importance descending
  const sortedFactors = [...decisionFactors].sort((a, b) => b.importance - a.importance);

  const getProgramLabel = (prog: PredictProgram): string => {
    const labels: Record<PredictProgram, string> = {
      [PredictProgram.HOME_HEALTH]: 'Home Health',
      [PredictProgram.HOSPITAL_AT_HOME]: 'Hospital at Home',
      [PredictProgram.SNF_AT_HOME]: 'SNF at Home',
      [PredictProgram.HOSPICE]: 'Hospice',
      [PredictProgram.INFUSION_AT_HOME]: 'Infusion at Home',
      [PredictProgram.PALLIATIVE]: 'Palliative Care',
    };
    return labels[prog];
  };

  const getScoreColor = (scoreVal: number): string => {
    // Green: 80-100 (high scores, closer to 100)
    if (scoreVal >= 80) return '#1B5E20';
    // Yellow/Orange: 60-79 (medium scores)
    if (scoreVal >= 60) return '#E65100';
    // Red: 0-59 (low scores)
    return '#B71C1C';
  };

  const getScoreBgColor = (scoreVal: number): string => {
    if (scoreVal >= 80) return '#E8F5E9';
    if (scoreVal >= 60) return '#FFF3E0';
    return '#FFEBEE';
  };

  const getImportanceColor = (importance: number, contribution: 'positive' | 'negative'): string => {
    // Positive factors use consistent green
    if (contribution === 'positive') {
      return '#4caf50'; // Green for all positive factors
    }

    // Negative factors use consistent red
    return '#d32f2f'; // Red for all negative factors
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getFactorSentiment = (importance: number, contribution: 'positive' | 'negative'): { icon: React.ReactNode; label: string; color: string } => {
    // Positive factors use thumbs up and consistent green
    if (contribution === 'positive') {
      if (importance >= 60) {
        return {
          icon: <ThumbUpIcon sx={{ fontSize: '1.1rem' }} />,
          label: 'Significant',
          color: '#4caf50',
        };
      }
      return {
        icon: <ThumbUpIcon sx={{ fontSize: '1.1rem' }} />,
        label: 'Minor',
        color: '#4caf50',
      };
    }

    // Negative factors use thumbs down and consistent red
    if (importance >= 60) {
      return {
        icon: <ThumbDownIcon sx={{ fontSize: '1.1rem' }} />,
        label: 'Significant',
        color: '#d32f2f',
      };
    }
    return {
      icon: <ThumbDownIcon sx={{ fontSize: '1.1rem' }} />,
      label: 'Minor',
      color: '#d32f2f',
    };
  };

  return (
    <Card
      sx={{
        mb: 2,
        border: isSelected ? '2px solid' : '1px solid',
        borderColor: isSelected ? 'success.main' : 'divider',
        backgroundColor: isSelected ? '#E8F5E9' : 'transparent',
        boxShadow: 0,
        position: 'relative',
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: isSelected ? 700 : 600 }}>
                {getProgramLabel(program)}
              </Typography>
              {isSelected && (
                <Chip
                  label="Selected"
                  size="small"
                  color="success"
                  sx={{ fontWeight: 600 }}
                  onDelete={onClearSelection}
                />
              )}
              {isRecommended && score >= 80 && (
                <Chip
                  label="Recommended"
                  size="small"
                  color="primary"
                  variant={isSelected ? 'outlined' : 'filled'}
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Box>
            <Chip
              label={score}
              sx={{
                fontWeight: 700,
                fontSize: '1rem',
                height: 32,
                backgroundColor: getScoreBgColor(score),
                color: getScoreColor(score),
              }}
            />
          </Box>
        }
        action={
          <IconButton onClick={() => setExpanded(!expanded)} size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        }
        sx={{
          backgroundColor: isSelected ? 'transparent' : 'grey.50',
          pb: 1,
        }}
      />
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent>
          {sortedFactors.length === 0 ? (
            <Typography color="grey.500">No decision factors available</Typography>
          ) : (
            <List sx={{ p: 0 }}>
              {sortedFactors.map((factor, index) => (
                <React.Fragment key={factor.id}>
                  <ListItem
                    sx={{
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      px: 0,
                      py: 2,
                    }}
                  >
                    <Box sx={{ width: '100%', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Box
                          sx={{
                            color: getFactorSentiment(factor.importance, factor.contribution).color,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {getFactorSentiment(factor.importance, factor.contribution).icon}
                        </Box>
                        <Typography variant="body2" fontWeight="bold">
                          {factor.label}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="grey.500">
                          {factor.dataSource} • {formatTimestamp(factor.timestamp)}
                        </Typography>
                        <Typography variant="caption" color="grey.600" sx={{ fontWeight: 600, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                          Importance Weight: <span style={{ fontWeight: 700, color: '#424242' }}>{factor.importance}%</span>
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ width: '100%', mb: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={factor.importance}
                        sx={{
                          height: 8,
                          borderRadius: 1,
                          backgroundColor: 'grey.300',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: getImportanceColor(factor.importance, factor.contribution),
                          },
                        }}
                      />
                    </Box>

                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Value:</strong> {factor.value}
                    </Typography>
                  </ListItem>

                  {index < sortedFactors.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
          {onSelect && !isSelected && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
              <Button
                variant="contained"
                size="small"
                color="primary"
                onClick={() => onSelect(program)}
              >
                Select
              </Button>
            </Box>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default ProgramFitCard;
